import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import JobLogsPanel from '../components/JobLogsPanel'
import PipelineVisualizer from '../components/PipelineVisualizer'
import StatusBadge from '../components/StatusBadge'
import EmptyState from '../components/ui/EmptyState'
import ErrorState from '../components/ui/ErrorState'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import SectionCard from '../components/ui/SectionCard'
import publicRecognitionAdminService from '../services/publicRecognitionAdminService'
import { formatDateTime } from '../utils/date'
import { formatConfidence, formatEmptyValue } from '../utils/displayValue'
import { ui } from '../styles/ui'

function JsonBlock({ value }) {
  return (
    <details>
      <summary className="cursor-pointer text-sm text-primary">Show JSON</summary>
      <pre className="mt-2 overflow-x-auto rounded border border-outline bg-surface-elevated p-3 text-xs text-foreground">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  )
}

function TruncatedWithHint({ value, fallback = '-', maxWidthClass = 'max-w-[340px]' }) {
  const text = value == null ? '' : String(value).trim()
  if (!text) return <span>{fallback}</span>
  return (
    <span
      className={`block overflow-hidden text-ellipsis whitespace-nowrap ${maxWidthClass}`.trim()}
      title={text}
    >
      {text}
    </span>
  )
}

export default function PublicRecognitionDetail() {
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [imagePreviewUrl, setImagePreviewUrl] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    publicRecognitionAdminService.getPublicRecognition(id)
      .then((next) => {
        if (!cancelled) setData(next)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err?.response?.data?.message || err.message || 'Failed to load recognition detail')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  useEffect(() => {
    let cancelled = false
    let objectUrl = ''
    const previewUrl = data?.input?.imagePreviewUrl
    setImagePreviewUrl('')
    if (!previewUrl) return undefined
    publicRecognitionAdminService.getPublicRecognitionAssetBlob(previewUrl)
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setImagePreviewUrl(objectUrl)
      })
      .catch(() => {
        if (!cancelled) setImagePreviewUrl('')
      })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [data?.input?.imagePreviewUrl])

  if (loading) return <PageShell><LoadingState text="Loading recognition details..." /></PageShell>
  if (error || !data) return <PageShell><ErrorState title="Failed to load" description={error || 'Recognition not found'} /></PageShell>

  const items = Array.isArray(data?.result?.items) ? data.result.items : []
  const logs = Array.isArray(data.logs) ? data.logs : []
  const pipeline = Array.isArray(data.pipeline) ? data.pipeline : []
  const artifacts = Array.isArray(data.artifacts) ? data.artifacts : []

  return (
    <PageShell>
      <PageHeader title="Public Recognition Detail" subtitle={data.id} />

      <SectionCard title="Summary">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div><div className={ui.forms.label}>Status</div><StatusBadge label="Status" status={data.status} /></div>
          <div><div className={ui.forms.label}>Source</div><div>{data.sourceType}</div></div>
          <div><div className={ui.forms.label}>Created</div><div>{formatDateTime(data.createdAt)}</div></div>
          <div><div className={ui.forms.label}>Processed</div><div>{formatEmptyValue(data.processedAt ? formatDateTime(data.processedAt) : null)}</div></div>
          <div><div className={ui.forms.label}>Updated</div><div>{formatEmptyValue(data.updatedAt ? formatDateTime(data.updatedAt) : null)}</div></div>
          <div><div className={ui.forms.label}>Duration</div><div>{data.durationMs != null ? `${data.durationMs} ms` : formatEmptyValue(null)}</div></div>
          <div><div className={ui.forms.label}>Confidence</div><div>{formatConfidence(data.confidence)}</div></div>
          <div><div className={ui.forms.label}>Request ID</div><div className="break-all">{formatEmptyValue(data.request?.requestId)}</div></div>
          <div><div className={ui.forms.label}>Client ID</div><div>{formatEmptyValue(data.request?.clientId)}</div></div>
          <div><div className={ui.forms.label}>Error</div><div>{formatEmptyValue(data.error?.message)}</div></div>
          <div><div className={ui.forms.label}>Termination reason</div><div>{formatEmptyValue(data.terminationReason)}</div></div>
        </div>
      </SectionCard>

      <SectionCard title="Parsed Receipt">
        {!data.result?.parsedJson ? <EmptyState title="No parsed receipt" /> : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div><div className={ui.forms.label}>Merchant</div><div>{formatEmptyValue(data.result.merchant)}</div></div>
              <div><div className={ui.forms.label}>Date</div><div>{formatEmptyValue(data.result.date)}</div></div>
              <div><div className={ui.forms.label}>Currency</div><div>{formatEmptyValue(data.result.currency)}</div></div>
              <div><div className={ui.forms.label}>Total</div><div>{formatEmptyValue(data.result.total)}</div></div>
            </div>
            <JsonBlock value={data.result.parsedJson} />
          </div>
        )}
      </SectionCard>

      <SectionCard title="Products">
        {items.length === 0 ? <EmptyState title="No products" /> : (
          <div className={ui.tables.wrapper}>
            <table className={ui.tables.table}>
              <thead className={ui.tables.head}>
                <tr>
                  <th className="pl-4 pr-2 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Original name</th>
                  <th className="pl-2 pr-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Translated name</th>
                  <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Qty</th>
                  <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Unit price</th>
                  <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Total</th>
                  <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Category</th>
                  <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Confidence</th>
                </tr>
              </thead>
              <tbody className={ui.tables.body}>
                {items.map((row, index) => (
                  <tr key={row?.id ?? index} className={ui.tableRow}>
                    <td className="pl-4 pr-2 py-2 align-top text-sm text-foreground">
                      <TruncatedWithHint value={row?.name} maxWidthClass="max-w-[280px]" />
                    </td>
                    <td className="pl-2 pr-4 py-2 align-top text-sm text-foreground">
                      <TruncatedWithHint value={row?.translatedName} maxWidthClass="max-w-[280px]" />
                    </td>
                    <td className="px-4 py-2 align-top text-sm text-foreground">{formatEmptyValue(row?.qty)}</td>
                    <td className="px-4 py-2 align-top text-sm text-foreground">{formatEmptyValue(row?.price)}</td>
                    <td className="px-4 py-2 align-top text-sm text-foreground">{formatEmptyValue(row?.total)}</td>
                    <td className="px-4 py-2 align-top text-sm text-foreground">{formatEmptyValue(row?.category)}</td>
                    <td className="px-4 py-2 align-top text-sm text-foreground">{formatEmptyValue(row?.confidence)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Pipeline">
        <PipelineVisualizer steps={pipeline} />
      </SectionCard>

      <SectionCard title="Logs">
        <JobLogsPanel logs={logs} />
      </SectionCard>

      <SectionCard title="Raw Data">
        <div className="space-y-3">
          <div>
            <div className={ui.forms.label}>Raw OCR text</div>
            {data.input?.rawText ? <JsonBlock value={data.input.rawText} /> : <div>{formatEmptyValue(null)}</div>}
          </div>
          <div>
            <div className={ui.forms.label}>Image preview</div>
            {imagePreviewUrl ? (
              <a href={imagePreviewUrl} target="_blank" rel="noreferrer" className="inline-block">
                <img
                  src={imagePreviewUrl}
                  alt="Public recognition preview"
                  className="max-h-64 rounded border border-outline object-contain"
                />
              </a>
            ) : (
              <div>{formatEmptyValue(null)}</div>
            )}
          </div>
          <div>
            <div className={ui.forms.label}>Raw QR data</div>
            <div className="break-all">{formatEmptyValue(data.input?.qrRaw)}</div>
          </div>
          <div>
            <div className={ui.forms.label}>Speech raw text</div>
            <div className="whitespace-pre-wrap break-words">{formatEmptyValue(data.input?.speechRaw)}</div>
          </div>
          <div>
            <div className={ui.forms.label}>Artifacts</div>
            {artifacts.length === 0 ? <div>{formatEmptyValue(null)}</div> : <JsonBlock value={artifacts} />}
          </div>
          <div>
            <div className={ui.forms.label}>Sanitized raw input</div>
            <JsonBlock value={data.input?.sanitizedRawInput || {}} />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Request Metadata">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div><div className={ui.forms.label}>Request ID</div><div>{formatEmptyValue(data.request?.requestId)}</div></div>
          <div><div className={ui.forms.label}>Client ID</div><div>{formatEmptyValue(data.request?.clientId)}</div></div>
          <div><div className={ui.forms.label}>Public API group</div><div>{formatEmptyValue(data.request?.publicApiEndpointGroup)}</div></div>
          <div><div className={ui.forms.label}>Public API action</div><div>{formatEmptyValue(data.request?.publicApiAction)}</div></div>
          <div><div className={ui.forms.label}>Subject type</div><div>{formatEmptyValue(data.request?.publicApiSubjectType)}</div></div>
          <div><div className={ui.forms.label}>Public API version</div><div>{formatEmptyValue(data.request?.publicApiVersion)}</div></div>
          <div>
            <div className={ui.forms.label}>IP hash</div>
            <div>
              <TruncatedWithHint value={data.request?.ipHash} />
            </div>
          </div>
          <div><div className={ui.forms.label}>User agent</div><div className="break-all">{formatEmptyValue(data.request?.userAgent)}</div></div>
          <div><div className={ui.forms.label}>Created at</div><div>{formatDateTime(data.createdAt)}</div></div>
        </div>
      </SectionCard>
    </PageShell>
  )
}

